import React from 'react';
import {
  Paper, Box, Typography, TextField, InputAdornment, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, Chip,
  useMediaQuery, CircularProgress, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('th-TH', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(date);
};

const statusColor = (status) => {
  const normalized = String(status || '').toUpperCase().replace(/[ -]+/g, '_');
  if (normalized === 'EXPIRED') return 'error';
  if (normalized === 'MUST_USE') return 'warning';
  if (normalized === 'PREPARING') return 'info';
  return 'success';
};

export default function ItemListCard({
  items = [], query = '', onQueryChange, location = '', onDelete,
  loading = false, deletingId = null,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const needle = query.trim().toLowerCase();
  const filtered = items.filter((item) => [item.product_name, item.scan_code, item.lot, item.batch]
    .some((value) => String(value || '').toLowerCase().includes(needle)));

  const removeButton = (item) => (
    <Tooltip title="ยกเลิกรายการสแกน">
      <span>
        <IconButton color="error" size="small" disabled={deletingId === item.id} onClick={() => onDelete?.(item)}>
          {deletingId === item.id ? <CircularProgress size={18} /> : <DeleteOutlineIcon />}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Paper sx={{ p: { xs: 1.25, md: 2 }, borderRadius: 2, boxShadow: 'none' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Inventory2OutlinedIcon sx={{ color: colors.primary[500] }} />
        <Typography sx={{ color: colors.primary[500], fontWeight: 700 }}>
          10 รายการล่าสุด {location ? `• ${location}` : ''}
        </Typography>
        <Chip size="small" label={`${filtered.length} รายการ`} sx={{ ml: 'auto' }} />
      </Box>

      <TextField
        fullWidth
        placeholder="ค้นหาชื่อสินค้า, Lot, Batch หรือ QR Code"
        value={query}
        onChange={(event) => onQueryChange?.(event.target.value)}
        size="small"
        sx={{ mb: 1.5 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
          endAdornment: query ? (
            <InputAdornment position="end">
              <IconButton aria-label="clear-search" size="small" onClick={() => onQueryChange?.('')}><ClearIcon /></IconButton>
            </InputAdornment>
          ) : null,
          sx: { borderRadius: 2, backgroundColor: '#f6f8fa' },
        }}
      />

      {loading ? (
        <Box sx={{ py: 6, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
          <Inventory2OutlinedIcon sx={{ fontSize: 48, opacity: 0.35 }} />
          <Typography>{query ? 'ไม่พบรายการที่ค้นหา' : 'ยังไม่มีสินค้าที่สแกนออกจากห้องนี้'}</Typography>
        </Box>
      ) : isMobile ? (
        <Box sx={{ display: 'grid', gap: 1 }}>
          {filtered.map((item, index) => (
            <Paper key={item.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700 }}>{index + 1}. {item.product_name || '-'}</Typography>
                  <Typography variant="body2" color="text.secondary">Lot {item.lot || '-'} • Batch {item.batch || '-'} • Shift {item.shift || '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">สแกนเมื่อ {formatDateTime(item.scanned_at)}</Typography>
                </Box>
                <Chip size="small" color={statusColor(item.status)} label={item.status || 'READY'} />
                {removeButton(item)}
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 900, border: '1px solid #dbe2e8' }}>
            <TableHead>
              <TableRow>
                {['No.', 'ชื่อสินค้า', 'Lot', 'Batch', 'Shift', 'Weight (kg)', 'Qty', 'สถานะ', 'เวลาที่สแกน', ''].map((label) => (
                  <TableCell key={label} sx={{ bgcolor: colors.primary[500], color: '#fff', fontWeight: 700 }}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((item, index) => (
                <TableRow key={item.id} hover>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{item.product_name || '-'}</TableCell>
                  <TableCell>{item.lot || '-'}</TableCell>
                  <TableCell>{item.batch || '-'}</TableCell>
                  <TableCell>{item.shift || '-'}</TableCell>
                  <TableCell>{item.weight ?? '-'}</TableCell>
                  <TableCell>{item.qty ?? '-'}</TableCell>
                  <TableCell><Chip size="small" color={statusColor(item.status)} label={item.status || 'READY'} /></TableCell>
                  <TableCell>{formatDateTime(item.scanned_at)}</TableCell>
                  <TableCell align="right">{removeButton(item)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}
